import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BrandMark from "../components/BrandMark";

const PRESET_AMOUNTS = {
  ILS: [18, 36, 54, 100, 180, 360],
  USD: [5, 10, 18, 36, 54, 100],
};

const CURRENCY_SYMBOL = { ILS: "₪", USD: "$" };

// Pick the localised title/description for a cause, with safe fallbacks
// to the Hebrew copy when the backend hasn't been updated yet.
function pickCauseTitle(c, isEn) {
  if (!c) return "";
  if (isEn) return c.title_en || c.title_he || "";
  return c.title_he || c.title_en || "";
}
function pickCauseDescription(c, isEn) {
  if (!c) return "";
  if (isEn) return c.description_en || c.description_he || "";
  return c.description_he || c.description_en || "";
}

export default function Donate() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const dir = isEn ? "ltr" : "rtl";
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [causes, setCauses] = useState([]);
  const [selectedCause, setSelectedCause] = useState("living_tanakh");
  const [currency, setCurrency] = useState("ILS");
  const [amount, setAmount] = useState(36);
  const [customAmount, setCustomAmount] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [dedicationType, setDedicationType] = useState("none");
  const [dedicationText, setDedicationText] = useState("");
  const [donorName, setDonorName] = useState(user?.name || "");
  const [donorEmail, setDonorEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);

  // ---- Cancel-from-Stripe handling ----
  // Stripe's cancel_url is /donate?canceled=1. The successful-payment
  // redirect now goes to /donate/thank-you, which handles its own
  // status polling — see pages/DonateThankYou.jsx.
  useEffect(() => {
    api
      .donationCauses()
      .then((d) => setCauses(d.items || []))
      .catch(() => setCauses([]));
  }, []);

  useEffect(() => {
    if (searchParams.get("canceled")) {
      toast.error(t("donate.toast_canceled"));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("canceled")]);

  const finalAmount = useMemo(() => {
    if (customAmount && Number(customAmount) > 0) return Number(customAmount);
    return amount;
  }, [customAmount, amount]);

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) {
      toast.error(t("donate.toast_amount_positive"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: finalAmount,
        currency,
        cause: selectedCause,
        recurring,
        dedication_type: dedicationType === "none" ? null : dedicationType,
        dedication_text: dedicationType === "none" ? null : dedicationText.trim() || null,
        donor_name: donorName.trim() || null,
        donor_email: donorEmail.trim() || null,
      };
      const r = await api.createDonationCheckout(payload, window.location.origin);
      if (r.url) {
        window.location.href = r.url;
      } else {
        toast.error(t("donate.toast_checkout_failed"));
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("donate.toast_process_error"));
      setSubmitting(false);
    }
  };

  const dedicationOptions = [
    { v: "none", label: t("donate.ded_none") },
    { v: "memory", label: t("donate.ded_memory") },
    { v: "honor", label: t("donate.ded_honor") },
    { v: "healing", label: t("donate.ded_healing") },
  ];

  const dedicationPlaceholder =
    dedicationType === "memory"
      ? t("donate.ded_placeholder_memory")
      : dedicationType === "honor"
      ? t("donate.ded_placeholder_honor")
      : t("donate.ded_placeholder_healing");

  const summaryDedicationLabel =
    dedicationType === "memory"
      ? t("donate.ded_memory")
      : dedicationType === "honor"
      ? t("donate.ded_honor")
      : t("donate.ded_healing");

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 md:py-16" data-testid="donate-page" dir={dir}>
      <header className="text-center mb-10 space-y-3">
        <div className="flex justify-center">
          <BrandMark size={56} className="rounded-xl shadow-md ring-1 ring-gold/30" />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl">{t("donate.title")}</h1>
        <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
          {t("donate.subtitle")}
        </p>
        <div className="ornamental-divider">✦</div>
      </header>

      {/* Cause selector */}
      <section className="mb-10">
        <h2 className="font-heading text-sm tracking-[0.25em] uppercase text-gold mb-4">
          {t("donate.section_cause")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {causes.map((c) => {
            const active = selectedCause === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedCause(c.id)}
                data-testid={`cause-card-${c.id}`}
                data-active={active}
                className={`text-start rounded-sm border p-5 transition-all ${
                  active
                    ? "border-gold ring-2 ring-gold/40 bg-gold/5"
                    : "border-border/60 hover:border-gold/60 hover:bg-card/40"
                }`}
              >
                <h3 className="font-heading text-lg text-foreground mb-2">{pickCauseTitle(c, isEn)}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{pickCauseDescription(c, isEn)}</p>
              </button>
            );
          })}
        </div>
      </section>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8" data-testid="donate-form">
        {/* Amount + dedication */}
        <div className="space-y-8">
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-sm tracking-[0.25em] uppercase text-gold">{t("donate.section_amount")}</h2>
              <Tabs value={currency} onValueChange={setCurrency} dir={dir}>
                <TabsList className="bg-transparent border border-border/60 rounded-sm p-1">
                  <TabsTrigger value="ILS" data-testid="currency-ils" className="text-xs px-3 min-h-[40px]">₪ ILS</TabsTrigger>
                  <TabsTrigger value="USD" data-testid="currency-usd" className="text-xs px-3 min-h-[40px]">$ USD</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {PRESET_AMOUNTS[currency].map((a) => {
                const active = !customAmount && amount === a;
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => {
                      setAmount(a);
                      setCustomAmount("");
                    }}
                    data-testid={`amount-preset-${a}`}
                    className={`rounded-sm border py-3 font-heading text-base transition-all ${
                      active
                        ? "border-gold bg-gold/15 text-gold"
                        : "border-border/60 text-foreground hover:border-gold/60"
                    }`}
                  >
                    {CURRENCY_SYMBOL[currency]}
                    {a}
                  </button>
                );
              })}
            </div>
            <div className="mt-3">
              <Label htmlFor="custom-amount" className="text-xs text-muted-foreground">{t("donate.custom_amount_label")}</Label>
              <Input
                id="custom-amount"
                data-testid="custom-amount"
                type="number"
                min="1"
                step="1"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder={`${CURRENCY_SYMBOL[currency]} 250`}
                className="mt-1"
              />
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer">
              <input
                type="checkbox"
                data-testid="recurring-toggle"
                checked={recurring}
                onChange={(e) => setRecurring(e.target.checked)}
                className="accent-gold"
              />
              <span className="text-sm text-foreground">{t("donate.recurring_label")}</span>
            </label>
          </section>

          <section>
            <h2 className="font-heading text-sm tracking-[0.25em] uppercase text-gold mb-3">{t("donate.section_dedication")}</h2>
            <RadioGroup
              value={dedicationType}
              onValueChange={setDedicationType}
              className="grid grid-cols-2 sm:grid-cols-4 gap-2"
              dir={dir}
            >
              {dedicationOptions.map((opt) => (
                <label
                  key={opt.v}
                  className={`rounded-sm border py-2 px-3 text-sm cursor-pointer text-center font-heading ${
                    dedicationType === opt.v
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-border/60 text-foreground hover:border-gold/60"
                  }`}
                >
                  <RadioGroupItem value={opt.v} className="sr-only" data-testid={`dedication-${opt.v}`} />
                  {opt.label}
                </label>
              ))}
            </RadioGroup>
            {dedicationType !== "none" && (
              <Textarea
                value={dedicationText}
                onChange={(e) => setDedicationText(e.target.value)}
                placeholder={dedicationPlaceholder}
                dir={dir}
                data-testid="dedication-text"
                className="mt-3 text-start"
              />
            )}
          </section>

          <section>
            <h2 className="font-heading text-sm tracking-[0.25em] uppercase text-gold mb-3">{t("donate.section_donor")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="donor-name" className="text-xs">{t("donate.donor_name_label")}</Label>
                <Input
                  id="donor-name"
                  data-testid="donor-name"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder={t("donate.donor_name_placeholder")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="donor-email" className="text-xs">{t("donate.donor_email_label")}</Label>
                <Input
                  id="donor-email"
                  data-testid="donor-email"
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Sticky summary — the visual anchor of the page. We treat
            this aside as the conversion surface: warmer gold background,
            soft gold-tinted shadow, a noticeably larger CTA than the
            rest of the form, and a reassuring "secured by Stripe"
            line directly below the button so the trust signal lives
            at the point of commitment. */}
        <aside
          className="lg:sticky lg:top-24 self-start rounded-sm
                     border border-gold/40
                     bg-gradient-to-b from-gold/[0.08] to-card/80
                     shadow-[0_8px_28px_-12px_rgba(184,137,59,0.35)]
                     p-6"
          data-testid="donate-summary"
        >
          <h3 className="font-heading text-sm tracking-[0.25em] uppercase text-gold mb-4">{t("donate.section_summary")}</h3>
          <dl className="space-y-3 text-sm">
            <Row label={t("donate.summary_cause")}>
              {pickCauseTitle(causes.find((c) => c.id === selectedCause), isEn) || "—"}
            </Row>
            <Row label={t("donate.summary_amount")}>
              <span className="font-heading text-2xl text-foreground">
                {CURRENCY_SYMBOL[currency]}
                {finalAmount}
              </span>
              {recurring && <span className="text-xs text-muted-foreground">{t("donate.per_month")}</span>}
            </Row>
            {dedicationType !== "none" && dedicationText && (
              <Row label={summaryDedicationLabel}>
                {dedicationText}
              </Row>
            )}
          </dl>
          <Button
            type="submit"
            disabled={submitting || !finalAmount}
            data-testid="submit-donation"
            className="w-full mt-6 sacred-btn py-7 text-base font-heading tracking-wide"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Heart className="h-5 w-5 me-2" strokeWidth={1.5} />
                {t("donate.donate_now")}
              </>
            )}
          </Button>
          <p
            className="text-[11px] text-muted-foreground mt-3 leading-relaxed
                       flex items-center justify-center gap-1.5"
            data-testid="donate-secure-payment"
          >
            <Lock className="h-3 w-3 text-gold/80" strokeWidth={2} />
            <span>{t("donate.secure_payment")}</span>
          </p>
        </aside>
      </form>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground text-end">{children}</dd>
    </div>
  );
}

/**
 * Maintenance screen shown while DONATIONS_PAUSED === true.
 *
 * Why this exists: A Stripe Live-mode misconfiguration (webhook
 * never fires) caused one donor to be charged $5 with no receipt
 * generated. Rather than risk additional charges-without-receipt
 * while we diagnose, we serve this card on /donate and refuse any
 * checkout attempts. Donors can still contact us by email.
 */
// eslint-disable-next-line no-unused-vars
function DonationsPausedScreen() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  return (
    <div
      dir={dir}
      data-testid="donate-maintenance"
      className="mx-auto max-w-xl px-4 sm:px-6 py-16 md:py-24 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center mb-6">
        <Heart className="h-6 w-6 text-gold" strokeWidth={1.6} />
      </div>
      <h1 className="font-heading text-3xl md:text-4xl text-foreground mb-3">
        {t("donate.paused_title")}
      </h1>
      <p className="text-foreground/85 leading-relaxed mb-2">
        {t("donate.paused_body_1")}
      </p>
      <p className="text-foreground/85 leading-relaxed mb-6">
        {t("donate.paused_body_2")}
      </p>

      <div className="rounded-sm border-e-4 border-gold/70 bg-gold/5 px-4 py-3 text-sm text-foreground/90 text-start">
        <p className="font-heading mb-1">{t("donate.paused_no_receipt_title")}</p>
        <p>
          {t("donate.paused_no_receipt_body")}
          {" "}
          <a
            href="mailto:7torahya@gmail.com"
            className="text-gold underline"
            data-testid="donate-maintenance-contact"
          >
            7torahya@gmail.com
          </a>
        </p>
      </div>

      <Button
        asChild
        variant="outline"
        size="sm"
        className="mt-8 rounded-full"
        data-testid="donate-maintenance-back"
      >
        <a href="/">{t("donate.paused_back_home")}</a>
      </Button>
    </div>
  );
}
